import { useState } from 'react';
import { libraryFetch } from '../../libraryFetch';
import './BookCheckout.scss';
import { toast } from 'react-toastify';

export default function BookCheckout({ book, onCancel, onComplete }) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const API_BASE = process.env.REACT_APP_API_URL || '';

    const handleConfirm = async () => {
        if (!book || !book.id) {
            setError('Error: Book ID is missing.');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await libraryFetch(
                `${API_BASE}/api/library/books/${book.id}/checkout`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error(
                    `Checkout failed: ${response.status}`
                );
            }

            // The API has successfully completed the checkout.
            if (onComplete) {
                onComplete();
                toast.success('Checkout Confirmed');
            }
        } catch (err) {
            console.error('Failed to check out book:', err);
            setError('Error: Failed to check out book');
        } finally {
            setIsLoading(false);
        }
    };

    if (!book) {
        return null;
    }

    return (
        <div className="book-checkout">

            {/* Left side: Book Information */}
            <div className="book-info-section">
                <div className="book-cover-placeholder">
                    <div className="cover-art">
                        {book.coverImage || book.cover ? (
                            <img
                                src={book.coverImage || book.cover}
                                alt={book.title}
                            />
                        ) : (
                            // NEEDS TO BE CHANGED:
                            // Salesforce/API does not currently provide a confirmed
                            // cover field. Do not use demo cover data.
                            <span>No Cover</span>
                        )}
                    </div>
                </div>

                <div className="book-details">
                    <h1>{book.title}</h1>

                    <p className="author">
                        By {book.author}
                    </p>

                    <p className="genre">
                        {book.genre} • {book.publicationYear}
                    </p>

                    <span className="badge-available">
                        Available
                    </span>
                </div>
            </div>

            <div className="checkout-details-section">
                <h2>Checkout Details</h2>

                <div className="date-pickers">

                    <div className="date-box">
                        <label className="date-label">
                            Checkout Date
                        </label>

                        <div className="date-input">
                            <div className="calendar-icon-box">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="size-6"
                                >
                                    <path d="M12 11.993a.75.75 0 0 0-.75.75v.006c0 .414.336.75.75.75h.006a.75.75 0 0 0 .75-.75v-.006a.75.75 0 0 0-.75-.75H12ZM12 16.494a.75.75 0 0 0-.75.75v.005c0 .414.335.75.75.75h.005a.75.75 0 0 0 .75-.75v-.005a.75.75 0 0 0-.75-.75H12ZM8.999 17.244a.75.75 0 0 1 .75-.75h.006a.75.75 0 0 1 .75.75v.006a.75.75 0 0 1-.75.75h-.006a.75.75 0 0 1-.75-.75v-.006ZM7.499 16.494a.75.75 0 0 0-.75.75v.005c0 .414.336.75.75.75h.005a.75.75 0 0 0 .75-.75v-.005a.75.75 0 0 0-.75-.75H7.5ZM13.499 14.997a.75.75 0 0 1 .75-.75h.006a.75.75 0 0 1 .75.75v.005a.75.75 0 0 1-.75.75h-.006a.75.75 0 0 1-.75-.75v-.005ZM14.25 16.494a.75.75 0 0 0-.75.75v.006c0 .414.335.75.75.75h.005a.75.75 0 0 0 .75-.75v-.006a.75.75 0 0 0-.75-.75h-.005ZM15.75 14.995a.75.75 0 0 1 .75-.75h.005a.75.75 0 0 1 .75.75v.006a.75.75 0 0 1-.75.75H16.5a.75.75 0 0 1-.75-.75v-.006ZM13.498 12.743a.75.75 0 0 1 .75-.75h2.25a.75.75 0 1 1 0 1.5h-2.25a.75.75 0 0 1-.75-.75ZM6.748 14.993a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1-.75-.75Z" />
                                    <path
                                        fillRule="evenodd"
                                        d="M18 2.993a.75.75 0 0 0-1.5 0v1.5h-9V2.994a.75.75 0 1 0-1.5 0v1.497h-.752a3 3 0 0 0-3 3v11.252a3 3 0 0 0 3 3h13.5a3 3 0 0 0 3-3V7.492a3 3 0 0 0-3-3H18V2.993ZM3.748 18.743v-7.5a1.5 1.5 0 0 1 1.5-1.5h13.5a1.5 1.5 0 0 1 1.5 1.5v7.5a1.5 1.5 0 0 1-1.5 1.5h-13.5a1.5 1.5 0 0 1-1.5-1.5Z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>

                            <span className="date-text">
                                {book.checkOutDate || new Date().toLocaleDateString(
                                    'en-US',
                                    {
                                        month: 'long',
                                        day: 'numeric',
                                        year: 'numeric'
                                    }
                                )}
                            </span>
                        </div>
                    </div>

                    <div className="date-box">
                        <label className="date-label">
                            Due Date
                        </label>

                        <div className="date-input">
                            <div className="calendar-icon-box">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="size-6"
                                >
                                    <path d="M12 11.993a.75.75 0 0 0-.75.75v.006c0 .414.336.75.75.75h.006a.75.75 0 0 0 .75-.75v-.006a.75.75 0 0 0-.75-.75H12ZM12 16.494a.75.75 0 0 0-.75.75v.005c0 .414.335.75.75.75h.005a.75.75 0 0 0 .75-.75v-.005a.75.75 0 0 0-.75-.75H12ZM8.999 17.244a.75.75 0 0 1 .75-.75h.006a.75.75 0 0 1 .75.75v.006a.75.75 0 0 1-.75.75h-.006a.75.75 0 0 1-.75-.75v-.006ZM7.499 16.494a.75.75 0 0 0-.75.75v.005c0 .414.336.75.75.75h.005a.75.75 0 0 0 .75-.75v-.005a.75.75 0 0 0-.75-.75H7.5ZM13.499 14.997a.75.75 0 0 1 .75-.75h.006a.75.75 0 0 1 .75.75v.005a.75.75 0 0 1-.75.75h-.006a.75.75 0 0 1-.75-.75v-.005ZM14.25 16.494a.75.75 0 0 0-.75.75v.006c0 .414.335.75.75.75h.005a.75.75 0 0 0 .75-.75v-.006a.75.75 0 0 0-.75-.75h-.005ZM15.75 14.995a.75.75 0 0 1 .75-.75h.005a.75.75 0 0 1 .75.75v.006a.75.75 0 0 1-.75.75H16.5a.75.75 0 0 1-.75-.75v-.006ZM13.498 12.743a.75.75 0 0 1 .75-.75h2.25a.75.75 0 1 1 0 1.5h-2.25a.75.75 0 0 1-.75-.75ZM6.748 14.993a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1-.75-.75Z" />
                                    <path
                                        fillRule="evenodd"
                                        d="M18 2.993a.75.75 0 0 0-1.5 0v1.5h-9V2.994a.75.75 0 1 0-1.5 0v1.497h-.752a3 3 0 0 0-3 3v11.252a3 3 0 0 0 3 3h13.5a3 3 0 0 0 3-3V7.492a3 3 0 0 0-3-3H18V2.993ZM3.748 18.743v-7.5a1.5 1.5 0 0 1 1.5-1.5h13.5a1.5 1.5 0 0 1 1.5-1.5v7.5a1.5 1.5 0 0 1-1.5 1.5h-13.5a1.5 1.5 0 0 1-1.5-1.5Z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>

                            <span className="date-text">
                                {book.dueDate || 'Due date provided by API'}
                            </span>
                        </div>
                    </div>
                </div>

                {error && (
                    <p className="checkout-error">
                        {error}
                    </p>
                )}

                {/* Action Buttons */}
                <div className="action-buttons">
                    <button
                        className="btn-cancel"
                        onClick={onCancel}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>

                    <button
                        className="btn-confirm"
                        onClick={handleConfirm}
                        disabled={isLoading}
                    >
                        {isLoading
                            ? 'Confirming...'
                            : 'Confirm Check out'}
                    </button>
                </div>
            </div>
        </div>
    );
}